import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IonIcon } from '@ionic/react';
import { chevronDownOutline, searchOutline, trashOutline } from 'ionicons/icons';
import CountryFlag from './CountryFlag';
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import { PortalPickerDropdown } from './PortalPickerDropdown';
import { usePortalPickerRect } from './usePortalPickerRect';
import styles from './PhoneAgentInput.module.scss';
// The type menu is the same TypePickerMenu InlineTypePicker uses (type rows plus
// the custom "Other" row), so phone and email type pickers behave identically.
import TypePickerMenu from './TypePickerMenu';
// Card styling for the type dropdown — same class InlineTypePicker's card uses.
import pickerStyles from './InlineTypePicker.module.scss';
import { useTranslation } from 'react-i18next';

// Phone input with an intl-tel-input style country selector living INSIDE the
// field. The text box DISPLAYS the national number formatted as-you-type
// ("(425) 754-4909"); the value emitted upward is plain E.164 ("+14257544909").
// Country is a local selection, never written into the text box.
//
// Optionally renders a label row (label + Make Primary / delete) above the
// field and a type picker (Mobile / Work / …) at the right edge of the field —
// this is the shape NewContact's phone rows need.

export interface PhoneTypeOption {
  value: string;
  label: string;
  icon: string;
}

interface Props {
  /** Canonical value — E.164 ("+14257544909") or empty. */
  value: string;
  onChange: (value: string) => void;
  /** Fixed, inset:0, pointer-events:none container the dropdowns portal into. */
  portalRef: React.RefObject<HTMLElement | null>;
  placeholder?: string;
  inputClassName?: string;
  /** Wrapper class — e.g. the page's `.fieldBox`. */
  boxClassName?: string;

  // ── Optional label row ────────────────────────────────────────────────
  label?: string;
  required?: boolean;
  hasError?: boolean;
  isPrimary?: boolean;
  onMakePrimary?: () => void;
  onDelete?: () => void;

  // ── Optional type picker (right edge of the field) ────────────────────
  typeOptions?: PhoneTypeOption[];
  currentType?: string;
  onTypeChange?: (type: string) => void;
}

const FlagIcon: React.FC<{ code: string }> = ({ code }) => (
  <CountryFlag
    countryCode={code}
    style={{ width: 18, height: 18, flexShrink: 0, borderRadius: 2 }}
  />
);

interface CountryEntry {
  code: CountryCode;
  name: string;
  dial: string;
}

const displayNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const COUNTRIES: CountryEntry[] = getCountries()
  .map((code) => ({
    code,
    name: displayNames?.of(code) ?? code,
    dial: `+${getCountryCallingCode(code)}`,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const US_ENTRY: CountryEntry =
  COUNTRIES.find((c) => c.code === 'US') ?? {
    code: 'US' as CountryCode,
    name: 'United States',
    dial: '+1',
  };

// Parse the canonical value into { country code, national digits }. Handles
// E.164 ("+14257544909"), a bare national number ("4257544909" → US), and the
// legacy "+1 425..." spaced form.
const parseValue = (
  raw: string,
): { code: CountryCode | null; nationalDigits: string } => {
  const trimmed = raw.trim();
  if (!trimmed) return { code: null, nationalDigits: '' };

  if (trimmed.startsWith('+')) {
    const parsed = parsePhoneNumberFromString(trimmed);
    if (parsed) {
      return { code: parsed.country ?? null, nationalDigits: parsed.nationalNumber };
    }
    const dials = [...new Set(COUNTRIES.map((c) => c.dial))].sort(
      (a, b) => b.length - a.length,
    );
    for (const dial of dials) {
      if (trimmed.startsWith(dial)) {
        const code = COUNTRIES.find((c) => c.dial === dial)?.code ?? null;
        return { code, nationalDigits: trimmed.slice(dial.length).replace(/\D/g, '') };
      }
    }
    return { code: null, nationalDigits: trimmed.replace(/\D/g, '') };
  }

  return { code: null, nationalDigits: trimmed.replace(/\D/g, '') };
};

type OpenMenu = 'none' | 'country' | 'type';

const PhoneAgentInput: React.FC<Props> = ({
  value,
  onChange,
  portalRef,
  placeholder = '(xxx) xxx-xxxx',
  inputClassName,
  boxClassName,
  label,
  required,
  hasError,
  isPrimary,
  onMakePrimary,
  onDelete,
  typeOptions,
  currentType,
  onTypeChange,
}) => {
  const { t: tr } = useTranslation();
  const [menu, setMenu] = useState<OpenMenu>('none');
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const countryTriggerRef = useRef<HTMLButtonElement>(null);
  const typeTriggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, menu]);

  const { nationalDigits } = useMemo(() => parseValue(value), [value]);

  // Country is a purely local selection. It's seeded ONCE from the initial value
  // and thereafter only changes via the picker — re-parsing the value as the
  // user types must never move the flag (partial numbers mis-infer countries).
  const [country, setCountry] = useState<CountryEntry>(() => {
    const { code } = parseValue(value);
    return COUNTRIES.find((c) => c.code === code) ?? US_ENTRY;
  });

  // Display: national digits formatted as-you-type for the current country.
  const displayValue = useMemo(() => {
    if (!nationalDigits) return '';
    return new AsYouType(country.code).input(nationalDigits);
  }, [nationalDigits, country.code]);

  // Emit plain E.164 ("+14257544909") upward, or "" when empty.
  const emitDigits = (digits: string, forCountry: CountryEntry = country) => {
    onChange(digits ? `+${getCountryCallingCode(forCountry.code)}${digits}` : '');
  };

  // Handle raw input. A backspace/delete that only removed a formatting
  // character (space, "-", "(", ")") leaves the digit count unchanged — the
  // formatter would just re-insert it and the key press looks dead. Detect that
  // and drop the digit that sat immediately before the deleted separator.
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const typedDigits = e.target.value.replace(/\D/g, '');
    const isDeletion = e.target.value.length < displayValue.length;
    if (isDeletion && typedDigits === nationalDigits && nationalDigits) {
      emitDigits(nationalDigits.slice(0, -1));
      return;
    }
    emitDigits(typedDigits);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dial.includes(q) ||
        c.code.toLowerCase() === q,
    );
  }, [search]);

  const close = useCallback(() => {
    setMenu('none');
    setSearch('');
  }, []);

  const pickCountry = (entry: CountryEntry) => {
    setCountry(entry);
    // Re-emit under the new dial code only if digits were already typed; an
    // empty field stays empty (no bare dial code leaks into the input).
    if (nationalDigits) emitDigits(nationalDigits, entry);
    close();
    requestAnimationFrame(() => phoneInputRef.current?.focus());
  };

  // ── Type picker ────────────────────────────────────────────────────────
  const typeNormalized = (currentType ?? '').toLowerCase();
  const knownType = typeOptions?.find((t) => t.value === typeNormalized);
  const typeTriggerLabel = knownType
    ? knownType.label
    : currentType
      ? currentType.charAt(0).toUpperCase() + currentType.slice(1).toLowerCase()
      : '';

  const selectType = (type: string) => {
    onTypeChange?.(type);
    close();
  };

  // The country dropdown is anchored under its trigger (aligned to the trigger's left) with the same
  // shared positioning the type menu uses (PortalPickerDropdown → usePortalPickerRect): flips upward
  // when there isn't room below and caps its height to the space available. The type menu is rendered
  // by the shared PortalPickerDropdown itself (same as InlineTypePicker).
  const pos = usePortalPickerRect(
    menu === 'country',
    countryTriggerRef as React.RefObject<HTMLElement | null>,
    portalRef as React.RefObject<HTMLElement | null>,
    dropdownRef,
    close,
    'left',
  );

  useEffect(() => {
    if (menu !== 'country') return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [menu, close]);

  const showTypePicker = !!(typeOptions && typeOptions.length && onTypeChange);

  const field = (
    <div
      className={`${styles.fieldBox} ${inputClassName ?? ''} ${
        hasError ? styles.fieldBoxError : ''
      }`}
    >
      <button
        type="button"
        ref={countryTriggerRef}
        className={`${styles.countryTrigger} ${
          menu === 'country' ? styles.countryTriggerOpen : ''
        }`}
        onClick={() => setMenu((m) => (m === 'country' ? 'none' : 'country'))}
        aria-label={tr('components.PhoneAgentInput.selectCountryCallingCode')}
      >
        <span className={styles.flag}>
          <FlagIcon code={country.code} />
        </span>
        <span className={styles.dialCode}>{country.dial}</span>
        <IonIcon icon={chevronDownOutline} className={styles.caret} />
      </button>

      <input
        ref={phoneInputRef}
        type="tel"
        inputMode="tel"
        value={displayValue}
        placeholder={placeholder}
        required={required}
        className={styles.fieldInput}
        onChange={handleInput}
      />

      {showTypePicker && (
        <button
          type="button"
          ref={typeTriggerRef}
          className={`${styles.typeTrigger} ${
            menu === 'type' ? styles.typeTriggerOpen : ''
          }`}
          onClick={() => setMenu((m) => (m === 'type' ? 'none' : 'type'))}
        >
          {typeTriggerLabel}
          <IonIcon icon={chevronDownOutline} className={styles.caret} />
        </button>
      )}
    </div>
  );

  return (
    <div ref={rootRef} className={`${styles.root} ${boxClassName ?? ''}`}>
      {(label || onMakePrimary || onDelete) && (
        <div className={styles.labelRow}>
          {label && <span className={styles.labelText}>{label}</span>}
          {(onMakePrimary || onDelete) && (
            <span className={styles.labelActions}>
              {onMakePrimary && (
                <span
                  role="button"
                  tabIndex={0}
                  className={`${styles.actionLink} ${
                    isPrimary ? styles.actionLinkPrimary : ''
                  }`}
                  onClick={onMakePrimary}
                >{tr('components.PhoneAgentInput.makePrimary')}
                </span>
              )}
              {onDelete && (
                <IonIcon
                  role="button"
                  tabIndex={0}
                  icon={trashOutline}
                  className={styles.actionDelete}
                  onClick={onDelete}
                />
              )}
            </span>
          )}
        </div>
      )}

      {field}

      {menu === 'country' &&
        pos &&
        portalNode &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdown}
            style={pos}
          >
            <div className={styles.searchRow}>
              <IonIcon icon={searchOutline} className={styles.searchIcon} />
              <input
                ref={searchInputRef}
                className={styles.searchInput}
                value={search}
                placeholder={tr('components.PhoneAgentInput.searchCountryOrCode')}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className={styles.countryList}>
              {filtered.map((c) => (
                <button
                  type="button"
                  key={c.code}
                  className={`${styles.countryItem} ${
                    country.code === c.code ? styles.countryItemActive : ''
                  }`}
                  onClick={() => pickCountry(c)}
                >
                  <span className={styles.flag}>
                    <FlagIcon code={c.code} />
                  </span>
                  <span className={styles.countryName}>{c.name}</span>
                  <span className={styles.countryDial}>{c.dial}</span>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className={styles.noResults}>{tr('components.PhoneAgentInput.noMatches')}</div>
              )}
            </div>
          </div>,
          portalNode,
        )}

      {showTypePicker && (
        <PortalPickerDropdown
          isOpen={menu === 'type'}
          fieldRef={typeTriggerRef as React.RefObject<HTMLElement | null>}
          portalRef={portalRef}
          close={close}
          align="right"
          className={pickerStyles.dropdown}
        >
          <TypePickerMenu
            types={typeOptions ?? []}
            currentType={currentType ?? ''}
            onSelect={selectType}
            onClose={close}
          />
        </PortalPickerDropdown>
      )}
    </div>
  );
};

export default PhoneAgentInput;
